import { Types } from './header';

const INITIAL_STATE = {
  metadata: [],
  missions: [],
  satellite: null,
};

/**
 * @function reduceAvailableDates
 * @description Applies a SET_AVAILABLE_DATES update to the state.
 * 1. If the payload includes `missions`, replaces both `availableDates` and `missions` directly.
 * 2. Otherwise, if the state already has missions, filters each mission's dates and the cached
 *    metadata down to those still present in the new available dates.
 * 3. Otherwise, just replaces `availableDates`.
 * @param {Object} state - Current MEPBay state.
 * @param {Object} payload - Action payload, with `dates` and optionally `missions`.
 * **Called by:
 * @see {reducer}
 * @memberof module:Mepbay
 * @returns {Object} The next MEPBay state.
 */
const reduceAvailableDates = (state, payload) => {
  console.log('Filtering per dates....');
  if (payload.missions !== undefined) {
    return {
      ...state,
      availableDates: payload.dates,
      missions: payload.missions,
    };
  }

  if (state.missions !== undefined) {
    const available = payload.dates;
    let missions = [];
    console.log('state.missions', state.missions);
    state.missions.forEach((mission) => {
      const dates = mission.dates;
      let filteredDates = {};

      Object.keys(dates).forEach((date) => {
        const cur = available.find((value) => value.date === date);
        if (cur) {
          filteredDates[date] = dates[date];
        }
      });

      missions.push({
        name: mission.name,
        shortname: mission.shortname,
        dates: filteredDates,
      });
    });

    const metadata = state.metadata.filter((meta) => {
      return available.find((value) => value.date === meta.date && value.name === meta.missionName);
    });

    return { ...state, metadata, availableDates: payload.dates, missions };
  }

  return { ...state, availableDates: payload.dates };
};

/**
 * @function reduceMissionFallback
 * @description Applies a SET_MISSION_FALLBACK update: replaces the failed mission in the current
 * satellite's mission list with its configured fallback mission.
 * @param {Object} state - Current MEPBay state.
 * @param {Object} action - Action payload, with a `mission` carrying a `fallbackMission`.
 * **Called by:
 * @see {reducer}
 * @memberof module:Mepbay
 * @returns {Object} The next MEPBay state, or the unchanged state if no satellite is selected.
 */
const reduceMissionFallback = (state, action) => {
  if (state.satellite) {
    const { mission } = action;

    const updated = state.satellite.missions.map((m) => (m.name === mission.name ? mission.fallbackMission : m));

    const satellite = {
      ...state.satellite,
      missions: updated,
      get: (name) => {
        const [first] = updated.filter((mission) => mission.name === name);
        return first;
      },
    };

    return { ...state, satellite };
  }
  return state;
};

/**
 * @function reducer
 * @description Redux reducer for the MEPBay duck. Handles satellite/mission-fallback selection,
 * AOI, available dates/missions, period, image metadata, and thumbnail-cache resets.
 * @param {Object} [state=INITIAL_STATE] - Current MEPBay state.
 * @param {Object} action - Dispatched Redux action.
 * @see {@link reduceAvailableDates}
 * @see {@link reduceMissionFallback}
 * **Called by:
 * @see {Redux store} - registered as the "mepbay" reducer.
 * @memberof module:Mepbay
 * @returns {Object} The next MEPBay state.
 */
export const reducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case Types.SET_SATELLITE:
      const { satellite, satelliteIndex } = action.payload;
      return { ...state, satellite, satelliteIndex };

    case Types.SET_MISSION_FALLBACK:
      return reduceMissionFallback(state, action.payload);

    case Types.SET_AOI:
      const { overlay, geometry, coordinates } = action.payload;
      return { ...state, overlay, geometry, coordinates };

    /**
     * See `ducks/acquisition/reducer.js` - `availableDatesVersion` counts completed searches so
     * `PeriodImageChooser` can tell one finished even when it returned the same (empty) date list
     * as the previous one.
     */
    case Types.SET_AVAILABLE_DATES:
      return {
        ...reduceAvailableDates(state, action.payload),
        availableDatesVersion: (state.availableDatesVersion || 0) + 1,
      };

    case Types.SET_PERIOD:
      return { ...state, start: action.payload.start, end: action.payload.end };

    case Types.INSERT_METADATA: {
      const metadata = [
        ...state.metadata,
        {
          ...action.payload.metadata,
          missionName: action.payload.missionName,
          date: action.payload.date,
        },
      ];

      return { ...state, metadata };
    }

    default:
      return state;
  }
};

export default reducer;
